import React, { useState, useEffect, useRef } from 'react';
import ChatMessage from './ChatMessage';
import AuthForm from './AuthForm';
import firebaseUserService from './FirebaseUserService';
import whatsAppService from './whatsAppService';

const Chatbot = ({ onSessionComplete }) => {
    const [messages, setMessages] = useState([]);
    const [userData, setUserData] = useState({ name: '', phone: '', email: '' });
    const [sessionData, setSessionData] = useState({});
    const [step, setStep] = useState('initial');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoadingAi, setIsLoadingAi] = useState(false);
    const [currentPathway, setCurrentPathway] = useState(null);
    const [isSendingToWhatsApp, setIsSendingToWhatsApp] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const chatEndRef = useRef(null);

    // Initialize Firebase service when component mounts
    useEffect(() => {
        const initializeFirebase = async () => {
            try {
                await firebaseUserService.initialize();
                console.log('✅ Firebase User Service initialized');
            } catch (error) {
                console.error('❌ Failed to initialize Firebase:', error);
            }
        };
        
        initializeFirebase();
    }, []);

    // --- Initial Bot Message ---
    useEffect(() => {
        addBotMessage("Namaste! Welcome. I am H.A.M.A, your Healthy Ageing Management Assistant. It is an honour to help you take this first step on your wellness journey.");
        setTimeout(() => {
            addBotMessage("The questions that follow will help me thoroughly understand your specific needs and goals so that I can connect you with our physiotherapist. I appreciate you taking the time to go through this with me.");
        }, 2000);
        setTimeout(() => {
            addBotMessage("Please feel free to complete this with a family member or carer if it helps. To get started, let's get you signed up or logged in.");
            setStep('askLoginOrSignup');
        }, 4000);
    }, []);

    // --- Auto-scroll Effect ---
    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // --- Message Handlers ---
    const addMessage = (text, sender, options = [], isAiPlan = false) => {
        setMessages(prev => [...prev, { text, sender, options, isAiPlan }]);
    };
    const addBotMessage = (text, options = []) => addMessage(text, 'bot', options);
    const addUserMessage = (text) => addMessage(text, 'user');

    // --- Enhanced Form Submission Handlers ---
    const handleSignup = async (name, phone) => {
        setIsLoading(true);
        
        try {
            const result = await firebaseUserService.signup(name, phone);
            
            if (result.success) {
                addUserMessage(`My name is ${name} and my phone is ${phone}`);
                setUserData({ 
                    name: result.user.name, 
                    phone: result.user.phone,
                    email: result.user.email || ''
                });
                setIsAuthenticated(true);
                addBotMessage(`✅ ${result.message}`);
                setStep('askEmail');
            } else {
                addBotMessage(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Signup error:', error);
            addBotMessage('❌ An unexpected error occurred during signup. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async (phone) => {
        setIsLoading(true);
        
        try {
            const result = await firebaseUserService.login(phone);
            
            if (result.success) {
                addUserMessage(`My phone number is ${phone}`);
                setUserData({ 
                    name: result.user.name, 
                    phone: result.user.phone,
                    email: result.user.email || ''
                });
                setIsAuthenticated(true);
                addBotMessage(`Welcome back, ${result.user.name}! It's wonderful to see you again.`);
                
                // Give user choice between questionnaire or gym
                setTimeout(() => {
                    addBotMessage("What would you like to do today?", [
                        { text: "Go through the health questionnaire again", value: "questionnaire" },
                        { text: "Go directly to Takeaway Gym", value: "gym" }
                    ]);
                    setStep('loginChoice');
                }, 1500);
            } else {
                addBotMessage(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Login error:', error);
            addBotMessage('❌ An unexpected error occurred during login. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    // --- New handler for switching between auth modes ---
    const handleAuthModeSwitch = (newMode) => {
        if (newMode === 'signup') {
            addUserMessage('New member? Sign Up');
            setStep('askSignup');
        } else {
            addUserMessage('Already a member? Login');
            setStep('askLogin');
        }
    };

    // --- Enhanced Email Handler ---
    const handleEmailSubmit = async (email) => {
        setIsLoading(true);
        
        try {
            const result = await firebaseUserService.updateUserEmail(email);
            
            if (result.success) {
                setUserData(prev => ({ ...prev, email }));
                addUserMessage(`My email is ${email}`);
                addBotMessage(`✅ ${result.message}`);
                setStep('coreModuleStart');
            } else {
                addBotMessage(`❌ ${result.message}`);
            }
        } catch (error) {
            console.error('Email update error:', error);
            addBotMessage('❌ Failed to update email. We can continue without it.');
            setStep('coreModuleStart');
        } finally {
            setIsLoading(false);
        }
    };

    // --- Core Module Logic ---
    useEffect(() => {
        if (step === 'askEmail' && isAuthenticated) {
            addBotMessage("If you have an email address, could you please share that too?", [
                { text: "I'll provide my email", value: "provide_email" },
                { text: "Skip email for now", value: "skip_email" }
            ]);
        } else if (step === 'coreModuleStart') {
            addBotMessage("Wonderful. Let's begin with a few simple details about your general wellbeing.");
            setTimeout(() => {
                setStep('healthRating');
            }, 1000);
        }
    }, [step, isAuthenticated]);

    // --- Question Flow Logic (keeping existing logic) ---
    const handleQuestionnaireOptionSelect = (option) => {
        addUserMessage(option.text);
        const currentStep = step;
        const value = option.value;
        setSessionData(prev => ({ ...prev, [currentStep]: value }));

        // --- Core Module Flow ---
        switch (currentStep) {
            case 'loginChoice':
                if (value === 'questionnaire') {
                    addBotMessage("Perfect! Let's go through the questionnaire to update your health profile.");
                    setTimeout(() => {
                        setStep('coreModuleStart');
                    }, 1000);
                } else {
                    addBotMessage("Taking you straight to our Takeaway Gym for exercise videos.");
                    setTimeout(() => {
                        onSessionComplete();
                    }, 2000);
                }
                break;

            case 'askEmail':
                if (value === 'provide_email') {
                    setStep('provideEmail');
                } else {
                    setStep('coreModuleStart');
                }
                break;

            case 'healthRating':
                setStep('medicalConditions');
                break;

            case 'medicalConditions':
                setStep('fallHistory');
                break;

            case 'fallHistory':
                if (value === 'Yes') {
                    setStep('fallDetails');
                } else {
                    setStep('fallWorry');
                }
                break;

            case 'fallDetails':
                setStep('fallWorry');
                break;

            case 'fallWorry':
                setStep('walkingAbility');
                break;

            case 'walkingAbility':
                if (['I walk independently.', 'I walk with supervision.', 'I walk with assistance.'].includes(value)) {
                    setStep('mobilityAid');
                } else {
                    setStep('exerciseComfort');
                }
                break;

            case 'mobilityAid':
                if (value === 'Yes') {
                    setStep('aidType');
                } else {
                    setStep('exerciseComfort');
                }
                break;

            case 'aidType':
                setStep('exerciseComfort');
                break;

            case 'exerciseComfort':
                if (value === 'No.') {
                    addBotMessage("I understand your concerns. Our physiotherapist will work with you to address any worries you may have about exercise.");
                }
                setStep('selectGoal');
                break;

            case 'selectGoal':
                setCurrentPathway(value);
                setSessionData(prev => ({ ...prev, goal: value }));
                switch (value) {
                    case 'wellness': setStep('wellness_activity_level'); break;
                    case 'pain': setStep('pain_location'); break;
                    case 'health': setStep('health_condition_type'); break;
                    case 'rehab': setStep('rehab_reason'); break;
                    case 'exploring': 
                        addBotMessage("Thank you for your interest! I'll provide you with some general information about our services.");
                        setStep('finalize');
                        break;
                    default: break;
                }
                break;

            // --- Pathway Flows (keeping existing pathway logic) ---
            // Wellness Pathway
            case 'wellness_activity_level': setStep('wellness_physical_tasks'); break;
            case 'wellness_physical_tasks': setStep('wellness_daily_activities'); break;
            case 'wellness_daily_activities': setStep('wellness_discomfort'); break;
            case 'wellness_discomfort':
                if (value === 'No, not really.') {
                    setStep('wellness_sleep');
                } else {
                    setStep('wellness_discomfort_location');
                }
                break;
            case 'wellness_discomfort_location': setStep('wellness_discomfort_rating'); break;
            case 'wellness_discomfort_rating': setStep('wellness_discomfort_type'); break;
            case 'wellness_discomfort_type': setStep('wellness_sleep'); break;
            case 'wellness_sleep': setStep('wellness_activity_goal'); break;
            case 'wellness_activity_goal': setStep('wellness_meaning'); break;
            case 'wellness_meaning': setStep('finalize'); break;

            // Pain Pathway
            case 'pain_location': setStep('pain_current_level'); break;
            case 'pain_current_level': setStep('pain_description'); break;
            case 'pain_description': setStep('pain_triggers'); break;
            case 'pain_triggers': setStep('pain_relief'); break;
            case 'pain_relief': setStep('pain_daily_impact'); break;
            case 'pain_daily_impact': setStep('pain_physical_tasks'); break;
            case 'pain_physical_tasks': setStep('pain_daily_activities'); break;
            case 'pain_daily_activities': setStep('pain_stress_factors'); break;
            case 'pain_stress_factors': setStep('pain_previous_treatments'); break;
            case 'pain_previous_treatments': setStep('finalize'); break;

            // Health Condition Pathway
            case 'health_condition_type': setStep('health_specific_condition'); break;
            case 'health_specific_condition': setStep('health_symptoms'); break;
            case 'health_symptoms': setStep('health_physical_tasks'); break;
            case 'health_physical_tasks': setStep('health_daily_activities'); break;
            case 'health_daily_activities': setStep('health_specialist'); break;
            case 'health_specialist': setStep('health_clearance'); break;
            case 'health_clearance': setStep('health_limitations'); break;
            case 'health_limitations': setStep('finalize'); break;

            // Rehabilitation Pathway
            case 'rehab_reason': setStep('rehab_specific_details'); break;
            case 'rehab_specific_details': setStep('rehab_clearance'); break;
            case 'rehab_clearance':
                if (value === 'No') {
                    addBotMessage("Thank you. It's important to get clearance from your doctor first. Please consult them before proceeding with any exercises.");
                    setStep('end_session_early');
                } else {
                    setStep('rehab_physical_tasks');
                }
                break;
            case 'rehab_physical_tasks': setStep('rehab_daily_activities'); break;
            case 'rehab_daily_activities': setStep('rehab_home_environment'); break;
            case 'rehab_home_environment': setStep('rehab_goal'); break;
            case 'rehab_goal': setStep('finalize'); break;

            default: break;
        }
    };

    // --- Render Next Question (keeping existing question map) ---
    useEffect(() => {
        const questionMap = {
            'healthRating': { 
                text: "On a scale of 1 to 10, where 1 is 'Very Poor' and 10 is 'Excellent', how would you rate your overall health today?", 
                options: Array.from({length: 10}, (_, i) => ({text: `${i + 1}`, value: i + 1}))
            },
            'medicalConditions': { 
                text: "Has a doctor ever mentioned that you have any of the following conditions? (Please select all that apply)", 
                options: [
                    {text: "High Blood Pressure", value: "High Blood Pressure"},
                    {text: "High Cholesterol", value: "High Cholesterol"},
                    {text: "Diabetes", value: "Diabetes"},
                    {text: "Thyroid issues", value: "Thyroid issues"},
                    {text: "Osteoporosis/Osteoarthritis", value: "Osteoporosis/Osteoarthritis"},
                    {text: "Heart or lung condition", value: "Heart or lung condition"},
                    {text: "Neurological condition", value: "Neurological condition"},
                    {text: "Autoimmune condition", value: "Autoimmune condition"},
                    {text: "None of the above", value: "None"}
                ]
            },
            'fallHistory': { 
                text: "Have you had a fall in the last year?", 
                options: [{text: "Yes", value: "Yes"}, {text: "No", value: "No"}]
            },
            'fallDetails': { 
                text: "Thank you for sharing. If you can recall, how many times have you fallen? Did any of those falls cause an injury?", 
                options: [
                    {text: "Once, no injury", value: "Once, no injury"},
                    {text: "Once, with injury", value: "Once, with injury"},
                    {text: "Multiple times, no injury", value: "Multiple times, no injury"},
                    {text: "Multiple times, with injury", value: "Multiple times, with injury"}
                ]
            },
            'fallWorry': { 
                text: "Do you ever worry about falling, even if you haven't?", 
                options: [
                    {text: "Yes, this is a big worry for me", value: "Yes, big worry"},
                    {text: "Sometimes, in certain situations", value: "Sometimes"},
                    {text: "No, I generally don't worry", value: "No worry"}
                ]
            },
            'walkingAbility': { 
                text: "Which of these best describes your ability to walk?", 
                options: [
                    {text: "I walk independently.", value: "I walk independently."},
                    {text: "I walk with supervision.", value: "I walk with supervision."},
                    {text: "I walk with assistance.", value: "I walk with assistance."},
                    {text: "I am unable to walk.", value: "I am unable to walk."}
                ]
            },
            'mobilityAid': { 
                text: "Do you use a mobility aid?", 
                options: [{text: "Yes", value: "Yes"}, {text: "No", value: "No"}]
            },
            'aidType': { 
                text: "What type of aid do you use? (Select all that apply)", 
                options: [
                    {text: "Walking Stick/Cane", value: "Walking Stick/Cane"},
                    {text: "Walker/Frame", value: "Walker/Frame"},
                    {text: "Wheelchair", value: "Wheelchair"},
                    {text: "Motorised Wheelchair/Scooter", value: "Motorised Wheelchair/Scooter"}
                ]
            },
            'exerciseComfort': { 
                text: "To help you on your journey, our plans often include gentle, guided exercises. Are you comfortable with the idea of participating in a light to moderate exercise program, with guidance from your physiotherapist?", 
                options: [
                    {text: "Yes, I am open to that.", value: "Yes"},
                    {text: "I'm not sure / I have some concerns.", value: "Unsure"},
                    {text: "No.", value: "No."}
                ]
            },
            'selectGoal': { 
                text: "Thank you for sharing that with me. To help us understand what's most important to you, could you please tell us what brings you here today?", 
                options: [
                    {text: "Provide me with a wellness and healthy ageing plan", value: "wellness"},
                    {text: "Help me manage my chronic pain", value: "pain"},
                    {text: "Help me manage my health condition", value: "health"},
                    {text: "Help with my reablement/rehabilitation program", value: "rehab"},
                    {text: "I am just exploring at the moment", value: "exploring"}
                ]
            },

            // Wellness Pathway Questions
            'wellness_activity_level': { 
                text: "Thank you. It's wonderful that you are focusing on your long-term wellness! Which of these best describes your usual day?", 
                options: [
                    {text: "I spend most of the day sitting", value: "sitting"},
                    {text: "I move around the house for daily chores", value: "house_chores"},
                    {text: "I take short walks outside most days", value: "short_walks"},
                    {text: "I do regular exercise a few times a week", value: "regular_exercise"}
                ]
            },
            'wellness_physical_tasks': { 
                text: "Now, let's get a clear picture of your current abilities. For getting out of bed, standing up from a chair, walking, reaching for objects, and climbing stairs - how much assistance do you typically need?", 
                options: [
                    {text: "No assistance for most tasks", value: "no_assistance"},
                    {text: "Minimal assistance for some tasks", value: "minimal_assistance"},
                    {text: "Moderate assistance for several tasks", value: "moderate_assistance"},
                    {text: "Maximum assistance needed", value: "maximum_assistance"}
                ]
            },
            'wellness_daily_activities': { 
                text: "And how about daily activities like dressing, bathing, using the toilet, eating, cooking, cleaning, and getting in/out of a car?", 
                options: [
                    {text: "Independent with all activities", value: "independent"},
                    {text: "Need some help with a few activities", value: "some_help"},
                    {text: "Need help with several activities", value: "several_help"},
                    {text: "Need significant help with most activities", value: "significant_help"}
                ]
            },
            'wellness_discomfort': { 
                text: "Do you experience any aches or discomfort that affect your mood or stop you from enjoying your day?", 
                options: [
                    {text: "Yes, quite often.", value: "Yes, quite often."},
                    {text: "Yes, sometimes.", value: "Yes, sometimes."},
                    {text: "No, not really.", value: "No, not really."}
                ]
            },
            'wellness_discomfort_location': { 
                text: "Thank you for letting me know. Where in your body do you usually feel this discomfort?", 
                options: [
                    {text: "Ankle", value: "Ankle"}, {text: "Knee", value: "Knee"}, {text: "Hip", value: "Hip"},
                    {text: "Lower Back", value: "Lower Back"}, {text: "Mid Back", value: "Mid Back"},
                    {text: "Neck", value: "Neck"}, {text: "Shoulder", value: "Shoulder"}
                ]
            },
            'wellness_discomfort_rating': { 
                text: "On a scale of 0 to 10 (0 is no discomfort, 10 is very severe), how would you rate it on a typical day?", 
                options: Array.from({length: 11}, (_, i) => ({text: `${i}`, value: i}))
            },
            'wellness_discomfort_type': { 
                text: "What does it usually feel like?", 
                options: [
                    {text: "A dull ache", value: "dull_ache"},
                    {text: "Stiffness", value: "stiffness"},
                    {text: "A sharp feeling", value: "sharp"},
                    {text: "Other", value: "other"}
                ]
            },
            'wellness_sleep': { 
                text: "How would you describe your sleep on most nights?", 
                options: [
                    {text: "Very restful and refreshing", value: "restful"},
                    {text: "Sometimes disturbed or I wake up tired", value: "sometimes_disturbed"},
                    {text: "Often poor and not refreshing", value: "poor"}
                ]
            },

            // Pain Pathway Questions
            'pain_location': { 
                text: "Thank you for trusting us to help with your pain. Where in your body do you feel this long-term pain?", 
                options: [
                    {text: "Ankle", value: "Ankle"}, {text: "Knee", value: "Knee"}, {text: "Hip", value: "Hip"},
                    {text: "Lower Back", value: "Lower Back"}, {text: "Middle Back", value: "Middle Back"},
                    {text: "Neck", value: "Neck"}, {text: "Shoulder", value: "Shoulder"}
                ]
            },
            'pain_current_level': { 
                text: "On a scale of 0 to 10 (0 is no pain, 10 is the worst pain imaginable), what is your pain level right now?", 
                options: Array.from({length: 11}, (_, i) => ({text: `${i}`, value: i}))
            },
            'pain_description': { 
                text: "What words best describe your pain?", 
                options: [
                    {text: "Dull Ache", value: "dull_ache"},
                    {text: "Sharp/Shooting", value: "sharp_shooting"},
                    {text: "Burning", value: "burning"},
                    {text: "Tingling/Numbness", value: "tingling"},
                    {text: "Stiff", value: "stiff"}
                ]
            },

            // Health Condition Pathway Questions
            'health_condition_type': { 
                text: "Thank you for sharing that. Which type of condition would you like the most help with?", 
                options: [
                    {text: "A Heart Condition", value: "heart"},
                    {text: "A Lung Condition", value: "lung"},
                    {text: "A Neurological Condition", value: "neurological"}
                ]
            },

            // Rehabilitation Pathway Questions
            'rehab_reason': { 
                text: "Thank you. We are here to support you on your recovery journey. What is the main reason for your rehabilitation?", 
                options: [
                    {text: "I am recovering from a surgery", value: "surgery"},
                    {text: "I am recovering from a medical event", value: "medical_event"},
                    {text: "I am recovering from an injury", value: "injury"},
                    {text: "I am recovering from an illness", value: "illness"}
                ]
            },
            'rehab_clearance': { 
                text: "Has your doctor cleared you to begin a light to moderate exercise program?", 
                options: [{text: "Yes", value: "Yes"}, {text: "No", value: "No"}]
            },
        };

        if (questionMap[step]) {
            addBotMessage(questionMap[step].text, questionMap[step].options);
        } else if (step === 'finalize') {
            finalizeSession();
        } else if (step === 'wellness_activity_goal') {
            addBotMessage("Is there any physical activity or hobby you would like to start or get back to? (e.g., gardening, dancing, joining a walking group)", [
                { text: "Yes, I have specific activities in mind", value: "has_activities" },
                { text: "Not sure yet", value: "not_sure" },
                { text: "No specific activities", value: "no_activities" }
            ]);
        } else if (step === 'wellness_meaning') {
            addBotMessage("What does 'healthy ageing' mean to you personally? (For example: 'Staying independent,' 'Having energy for my family,' 'Being free of pain.')", [
                { text: "Staying independent", value: "independence" },
                { text: "Having energy for family", value: "energy_family" },
                { text: "Being free of pain", value: "pain_free" },
                { text: "Maintaining my hobbies", value: "hobbies" },
                { text: "Other personal meaning", value: "other_meaning" }
            ]);
        }
    }, [step]);

    // --- Enhanced Session Finalization ---
    const finalizeSession = () => {
        addBotMessage("We have reached the end of the questions. Thank you for sharing so much with me.");
        
        setTimeout(() => {
            addBotMessage("Is there anything else on your mind, or anything at all you would like to add that you feel is important for us to know? Your thoughts are very valuable to us.", [
                { text: "Yes, I have something to add", value: "has_additional" },
                { text: "No, I think we've covered everything", value: "no_additional" }
            ]);
            setStep('final_thoughts');
        }, 2000);
    };

    const handleFinalThoughts = (option) => {
        addUserMessage(option.text);
        
        addBotMessage("Thank you so much for completing the questionnaire. This detailed information is incredibly valuable and gives us a wonderful foundation to build upon.");
        
        setTimeout(() => {
            addBotMessage("I will now securely send your responses to our expert physiotherapy team. They will personally review everything and contact you within 48 hours to get you started on your journey.");
            sendAssessmentToWhatsApp();
        }, 2000);

        setTimeout(() => {
            addBotMessage("We are excited to begin this with you and wish you all the best! Now, let me show you some exercise videos while you wait.", [
                { text: "✨ Get Personalized Recommendations", value: "get_ai_plan" },
                { text: "Go to Exercise Videos", value: "skip_ai_plan" }
            ]);
            setStep('ask_ai_plan');
        }, 6000);
    };

    // --- Enhanced WhatsApp Integration ---
    const sendAssessmentToWhatsApp = async () => {
        setIsSendingToWhatsApp(true);
        
        try {
            // Update WhatsApp number if needed
            whatsAppService.updateWhatsAppNumber('+61424833071');
            
            const result = await whatsAppService.sendAssessment(userData, sessionData, currentPathway, 'web');
            
            if (result.success) {
                console.log('✅ Assessment processed successfully:', result);
                addBotMessage("📱 Your assessment has been securely saved and prepared for our physiotherapy team.");
                
                // Optional: Show WhatsApp URL for manual sending
                if (result.whatsappUrl) {
                    console.log('🔗 WhatsApp URL:', result.whatsappUrl);
                }
            } else {
                console.error('❌ Assessment processing failed:', result.message);
                addBotMessage("⚠️ There was an issue preparing your assessment, but your responses have been saved. Our team will contact you soon.");
            }
        } catch (error) {
            console.error('WhatsApp integration error:', error);
            addBotMessage("⚠️ Technical issue with our messaging system, but your assessment is safely stored. Our team will contact you within 48 hours.");
        } finally {
            setIsSendingToWhatsApp(false);
        }
    };

    const handleAiChoice = (option) => {
        addUserMessage(option.text);
        if (option.value === 'get_ai_plan') {
            getAiRecommendations();
        } else {
            transitionToGym();
        }
    };

    const getAiRecommendations = async () => {
        setIsLoadingAi(true);
        addBotMessage("✨ H.A.M.A. is consulting with our AI assistant to personalize your plan based on your detailed assessment...");

        try {
            // Simulate AI response (replace with real API call if available)
            setTimeout(() => {
                const mockResponse = `Here is your personalized plan:

Based on your assessment, I've selected these exercises for you:

Gentle Seated Exercises for Seniors
Perfect for building strength safely while maintaining comfort. These exercises will help improve your mobility without putting stress on your joints.

10 Minute Chair Yoga
Excellent for flexibility and balance. This gentle practice will help you feel more stable and reduce stiffness throughout your day.

Standing Exercises for Fall Prevention
These carefully designed movements will help build the confidence and strength you need to feel secure on your feet.

Remember to start slowly and listen to your body. Your physiotherapist will guide you through proper techniques when they contact you.

You're taking wonderful steps toward your health goals!`;

                addMessage(mockResponse, 'bot', [], true);
                setIsLoadingAi(false);
                setTimeout(() => {
                    transitionToGym();
                }, 3000);
            }, 3000);
        } catch (error) {
            console.error("AI recommendation error:", error);
            addBotMessage("I had trouble creating your personalized plan, but let's head to the exercise videos where you can explore all options.");
            setIsLoadingAi(false);
            transitionToGym();
        }
    };

    const transitionToGym = () => {
        setTimeout(() => {
            addBotMessage("Now, let's head over to our Takeaway Gym for some helpful exercise videos.");
        }, 2000);

        setTimeout(() => {
            onSessionComplete();
        }, 4000);
    };

    const masterOptionHandler = (option) => {
        if (step === 'final_thoughts') {
            handleFinalThoughts(option);
        } else if (step === 'ask_ai_plan') {
            handleAiChoice(option);
        } else if (step === 'provideEmail') {
            // Handle email input separately
            setStep('coreModuleStart');
        } else {
            handleQuestionnaireOptionSelect(option);
        }
    };

    const OptionButton = ({ children, onSelect, isAiButton, disabled }) => (
        <button 
            style={{
                ...(isAiButton ? styles.aiOptionButton : styles.optionButton),
                ...(disabled ? styles.disabledButton : {})
            }} 
            onClick={onSelect}
            disabled={disabled}
        >
            {children}
        </button>
    );

    const renderCurrentStep = () => {
        if (isAuthenticated && step !== 'provideEmail') return null;

        switch (step) {
            case 'askLoginOrSignup':
                return (
                    <div style={styles.optionsContainer}>
                        <OptionButton 
                            onSelect={() => { addUserMessage('Sign Up'); setStep('askSignup'); }}
                            disabled={isLoading}
                        >
                            Sign Up
                        </OptionButton>
                        <OptionButton 
                            onSelect={() => { addUserMessage('Login'); setStep('askLogin'); }}
                            disabled={isLoading}
                        >
                            Login
                        </OptionButton>
                    </div>
                );
            case 'askSignup':
                return (
                    <AuthForm 
                        type="signup" 
                        onSubmit={handleSignup} 
                        onError={(error) => addBotMessage(`❌ ${error}`)}
                        onSwitchMode={handleAuthModeSwitch}
                        isLoading={isLoading}
                    />
                );
            case 'askLogin':
                return (
                    <AuthForm 
                        type="login" 
                        onSubmit={handleLogin} 
                        onError={(error) => addBotMessage(`❌ ${error}`)}
                        onSwitchMode={handleAuthModeSwitch}
                        isLoading={isLoading}
                    />
                );
            case 'provideEmail':
                return (
                    <EmailForm 
                        onSubmit={handleEmailSubmit}
                        isLoading={isLoading}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div style={styles.chatContainer}>
            <div style={styles.messageList}>
                {messages.map((msg, index) => (
                    <ChatMessage key={index} message={msg} onOptionSelect={masterOptionHandler} />
                ))}
                {isLoadingAi && <div style={styles.typingIndicator}>H.A.M.A. is creating your personalized plan...</div>}
                {isSendingToWhatsApp && <div style={styles.typingIndicator}>📱 Securely processing your assessment...</div>}
                {isLoading && <div style={styles.typingIndicator}>Processing...</div>}
                <div style={styles.formContainer}>
                    {renderCurrentStep()}
                </div>
                <div ref={chatEndRef} />
            </div>
        </div>
    );
};

// Enhanced Email Form Component
const EmailForm = ({ onSubmit, isLoading }) => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        
        if (!email.trim()) {
            setError('Please enter a valid email address');
            return;
        }
        
        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError('Please enter a valid email address');
            return;
        }
        
        onSubmit(email.trim());
    };

    return (
        <form onSubmit={handleSubmit} style={styles.authForm}>
            {error && <div style={styles.errorMessage}>{error}</div>}
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your.email@example.com"
                style={styles.input}
                required
                disabled={isLoading}
            />
            <button type="submit" style={styles.submitButton} disabled={isLoading}>
                {isLoading ? 'Updating...' : 'Submit Email'}
            </button>
        </form>
    );
};

// --- Styles ---
const styles = {
    chatContainer: {
        width: '100%',
        maxWidth: '600px',
        height: '95vh',
        maxHeight: '800px',
        backgroundColor: 'white',
        borderRadius: '12px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    messageList: {
        flex: 1,
        padding: '20px',
        overflowY: 'auto',
    },
    optionsContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        marginTop: '10px',
        gap: '8px',
        width: '100%',
    },
    optionButton: {
        backgroundColor: '#fff',
        border: '2px solid #007aff',
        color: '#007aff',
        padding: '12px 20px',
        borderRadius: '20px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: '600',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        width: 'fit-content',
    },
    aiOptionButton: {
        backgroundColor: '#007aff',
        border: '2px solid #007aff',
        color: '#fff',
        padding: '14px 22px',
        borderRadius: '22px',
        cursor: 'pointer',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        transition: 'all 0.2s ease',
        textAlign: 'center',
        width: 'fit-content',
        boxShadow: '0 2px 5px rgba(0, 122, 255, 0.3)',
    },
    formContainer: {
        padding: '10px',
        display: 'flex',
        justifyContent: 'center',
    },
    authForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        width: '100%',
        maxWidth: '300px',
        backgroundColor: '#f9f9f9',
        padding: '20px',
        borderRadius: '12px',
        border: '1px solid #ddd',
    },
    input: {
        padding: '12px',
        fontSize: '1rem',
        borderRadius: '8px',
        border: '1px solid #ccc',
    },
    submitButton: {
        backgroundColor: '#007aff',
        color: 'white',
        padding: '12px',
        fontSize: '1rem',
        borderRadius: '8px',
        border: 'none',
        cursor: 'pointer',
        fontWeight: 'bold',
    },
    typingIndicator: {
        fontStyle: 'italic',
        color: '#666',
        padding: '10px 20px',
        textAlign: 'left',
    },
    errorMessage: {
        backgroundColor: '#ffebee',
        color: '#c62828',
        padding: '10px',
        borderRadius: '6px',
        fontSize: '0.9rem',
        border: '1px solid #ffcdd2',
    },
};

export default Chatbot;